/**
 * 11 §2 hitstop as a render clock. Wall-clock driven, pure (timestamps are
 * passed in). The world render time `renderTime` holds during the freeze and
 * catches up over FX_HITSTOP_CATCHUP_MS afterwards; it never goes backward.
 */
import { FX_FLASH_FRAMES, FX_HITSTOP_CATCHUP_MS, FX_HITSTOP_HUB_MS, FX_HITSTOP_MAX_MS, FX_HITSTOP_MS, FX_HITSTOP_REDUCED_MS, FX_SHAKE_AMP_PX, FX_SHAKE_MS } from './constants.js'

export interface EliminationBurst { countA: number; countB: number; hub: boolean; reducedMotion: boolean; flashReduction?: boolean; hubHitstop?: boolean }

export interface ClockFrame {
  renderTime: number
  frozen: boolean
  /** Accent multiply flash (frames 0..FX_FLASH_FRAMES-1), null when none. */
  flash: { team: 0 | 1 | 'alt'; frameIndex: number } | null
  /** The ink-black stroke-only frame after the flash. */
  strokeOnlyFrame: boolean
  shake: { x: number; y: number }
  /** ms remaining in the freeze (0 when released). */
  remainingMs: number
  /** Wall time of the (last) hitstop release = T0 for un-draws (11 §3.2). */
  releaseAt: number
}

export class RenderClock {
  /** Wall time at which the current/last hitstop releases. */
  releaseAt = 0
  private startAt = 0
  private burstStart = 0
  private lastRender = 0
  private lastServer = 0
  /** Catch-up: at release, renderTime lags serverNow by `lagAtRelease`; ramp to 0 over FX_HITSTOP_CATCHUP_MS. */
  private lagAtRelease = 0
  private flashTeam: 0 | 1 | 'alt' | null = null
  private flashFramesLeft = 0
  private strokeOnlyLeft = 0
  private shakeStartAt = -Infinity
  private shakeAmp = 0
  private shakeMs = FX_SHAKE_MS
  private shakeEnabled = true
  private initialised = false

  get active(): boolean { return this.lastNow < this.releaseAt }
  private lastNow = 0

  /** Eliminations confirmed by one snapshot. */
  onEliminations(now: number, b: EliminationBurst): void {
    const total = b.countA + b.countB
    if (total <= 0) return
    const base = b.hub ? (b.hubHitstop === false ? 0 : FX_HITSTOP_HUB_MS) : b.reducedMotion ? FX_HITSTOP_REDUCED_MS : FX_HITSTOP_MS
    const running = now < this.releaseAt
    if (!running) {
      this.startAt = now
      this.burstStart = now
      this.releaseAt = now + base
      if (!b.hub) {
        if (!b.flashReduction) {
          // Flash uses the accent of the team that lost more; equal → alternate.
          this.flashTeam = b.countA > b.countB ? 0 : b.countB > b.countA ? 1 : 'alt'
          this.flashFramesLeft = FX_FLASH_FRAMES
          this.strokeOnlyLeft = 1
        }
        this.shakeEnabled = !b.reducedMotion
      } else this.shakeEnabled = false
    } else {
      // Extend: max(remaining, 0.5 × hitstop), capped per burst. No second flash.
      const remaining = this.releaseAt - now
      const ext = Math.max(remaining, base * 0.5)
      this.releaseAt = Math.min(this.burstStart + FX_HITSTOP_MAX_MS, now + ext)
    }
    this.pendingShake = !b.hub && !b.reducedMotion
  }
  private pendingShake = false

  /** 11 §10: a rescind while the hitstop is active ends it immediately, no shake. */
  rescind(now: number): void {
    if (now < this.releaseAt) { this.releaseAt = now; this.pendingShake = false; this.flashFramesLeft = 0; this.strokeOnlyLeft = 0 }
  }

  /** Small clash shake (11 §4): amp × 0.33 over 100 ms. */
  kick(now: number, ampMul = 0.33, ms = 100, reducedMotion = false): void {
    if (reducedMotion) return
    this.shakeStartAt = now; this.shakeAmp = FX_SHAKE_AMP_PX * ampMul; this.shakeMs = ms
  }

  update(now: number, serverNow: number): ClockFrame {
    this.lastNow = now
    if (!this.initialised) { this.initialised = true; this.lastRender = serverNow; this.lastServer = serverNow }
    const frozen = now < this.releaseAt
    let renderTime: number
    if (frozen) {
      renderTime = this.lastRender
      // Keep track of how far behind we are falling.
      this.lagAtRelease = serverNow - this.lastRender
      this.releasedHandled = false
    } else {
      if (!this.releasedHandled) {
        this.releasedHandled = true
        this.lagAtRelease = serverNow - this.lastRender
        this.catchupStart = this.releaseAt
        if (this.pendingShake) { this.shakeStartAt = this.releaseAt; this.shakeAmp = FX_SHAKE_AMP_PX; this.shakeMs = FX_SHAKE_MS; this.pendingShake = false }
      }
      const f = Math.min(1, (now - this.catchupStart) / FX_HITSTOP_CATCHUP_MS)
      renderTime = serverNow - this.lagAtRelease * (1 - f)
    }
    if (renderTime < this.lastRender) renderTime = this.lastRender
    this.lastRender = renderTime
    this.lastServer = serverNow
    // Flash frames count down per rendered frame while frozen.
    let flash: ClockFrame['flash'] = null
    let strokeOnly = false
    if (this.flashFramesLeft > 0) { flash = { team: this.flashTeam ?? 0, frameIndex: FX_FLASH_FRAMES - this.flashFramesLeft }; this.flashFramesLeft-- }
    else if (this.strokeOnlyLeft > 0) { strokeOnly = true; this.strokeOnlyLeft-- }
    // Shake: decaying sine, two axes with different frequencies, from release.
    let shake = { x: 0, y: 0 }
    const st = now - this.shakeStartAt
    if (this.shakeEnabled !== false && st >= 0 && st < this.shakeMs && this.shakeAmp > 0) {
      const decay = 1 - st / this.shakeMs
      shake = { x: Math.sin((st / 1000) * 11 * Math.PI * 2) * this.shakeAmp * decay, y: Math.sin((st / 1000) * 13 * Math.PI * 2) * this.shakeAmp * decay * 0.8 }
    }
    return { renderTime, frozen, flash, strokeOnlyFrame: strokeOnly, shake, remainingMs: Math.max(0, this.releaseAt - now), releaseAt: this.releaseAt }
  }
  private releasedHandled = true
  private catchupStart = 0

  /** For the debug overlay (14 R1). */
  get hitstopActive(): boolean { return this.active }
  get startedAt(): number { return this.startAt }
}
